/**
 * useLobbySocket — single source of truth for the lobby's realtime view.
 *
 * Manages the WebSocket lifecycle, dispatches events into reducer-style
 * state, and exposes typed sender functions for every action. Persists
 * `{ roomCode, charId, lastSeq }` to localStorage on every server event so
 * a reload mid-game can request a replay via lobby.replay.
 */
import { useEffect, useReducer, useRef } from 'react';
import type { LobbyView, ServerEvent, RoundResult } from '@tt/lobby';
import type { JukeboxState } from '@tt/lobby/records';
import { feedback } from '@tt/feedback';
import { joinLobby } from '../../lib/api';

const REJOIN_KEY = 'tt:rejoin';

interface RejoinSnapshot { roomCode: string; charId: string; lastSeq: number; ts: number }

export function loadRejoin(): RejoinSnapshot | null {
  try {
    const raw = localStorage.getItem(REJOIN_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as RejoinSnapshot;
    if (!s.roomCode || !s.charId) return null;
    if (Date.now() - s.ts > 4 * 60 * 60 * 1000) return null;
    return s;
  } catch { return null; }
}
export function clearRejoin(): void {
  try { localStorage.removeItem(REJOIN_KEY); } catch { /* */ }
}
function saveRejoin(s: RejoinSnapshot): void {
  try { localStorage.setItem(REJOIN_KEY, JSON.stringify(s)); } catch { /* */ }
}

export interface ChatMsg { charId: string; name: string; text: string; at: string }
export interface Reaction { id: number; charId: string; name: string; emoji: string; at: number }
export interface VoteState { gameIds: string[]; endsAt: number; tally: Record<string, number> }
/** Latest waiting-room walk-around position for a remote player. Transient
 *  (never persisted; cleared on lobby-state changes that leave `waiting`). */
export interface MoveState { charId: string; x: number; z: number; yaw: number; moving: boolean; at: number }
/** A hall-toy trigger (dartboard / dice / clink). Transient like {@link MoveState};
 *  `seed` deterministically drives the shared animation outcome on every client. */
export interface ToyUse { charId: string; name: string; toyId: string; seed: number }
export interface FinalStandings {
  standings: Array<{ charId: string; name: string; points: number }>;
  awards: Array<{ charId: string; label: string }>;
  winnerHue: number;
}

/** RT fast-lane bookkeeping (Total-Redesign-Spec §1d a3). Lives entirely
 *  in a ref — never in React state — so opening/closing the lane never
 *  re-renders the lobby. `url`/`token` are retained so a dropped lane can
 *  transparently reconnect (the token stays valid until matchEndsAt+grace). */
interface RtLane {
  ws: WebSocket;
  matchId: string;
  attempts: number;
  pingTimer: number | null;
  reconnectTimer: number | null;
  closedByUs: boolean;
  url: string;
  token: string;
  /** Lobby lanes only: set when rt.welcome advertised the 'presence'
   *  capability. sendMove refuses the lane until then (deploy-order
   *  safety — an older gameroom accepts the socket but drops frames). */
  presenceReady?: boolean;
}

export interface State {
  connected: boolean;
  /** True while the hook is actively trying to re-establish a dropped
   *  connection (between a non-clean WS close and a successful re-open).
   *  R1#7 — surfaced so consumers can render a "Reconnecting…" banner
   *  rather than a dead-silent UI. */
  reconnecting: boolean;
  /** True after the reconnect-attempt cap has been exhausted. The page
   *  reload escape valve in the parent should respond to this. */
  failed: boolean;
  lobby: LobbyView | null;
  chat: ChatMsg[];
  reactions: Reaction[];
  error: string | null;
  kicked: string | null;
  /** Current game match. */
  currentMatch: { matchId: string; gameId: string; startsAt: number; endsAt: number; params: unknown } | null;
  /** Last tick payload from the server, raw. */
  lastTick: { matchId: string; payload: unknown } | null;
  /** Last round result. */
  lastResult: RoundResult | null;
  vote: VoteState | null;
  /** Set has ended; show final standings pane. */
  finalStandings: FinalStandings | null;
  /** Tutorial card currently showing. */
  tutorial: { gameId: string; headline: string; tip: string } | null;
  /** Current in-game phase (multi-phase games). */
  currentPhase: { matchId: string; phase: string; phaseEndsAt?: number; payload?: unknown } | null;
  /** "Your Own Tavern" Phase B — jukebox queue + now-playing pick. Seeded
   *  from lobby.state (late-joiner) then updated by jukebox.state events. */
  jukebox: JukeboxState | null;
}

type Action =
  | { type: 'connected'; v: boolean }
  | { type: 'reconnecting'; v: boolean }
  | { type: 'failed' }
  | { type: 'error'; code: string }
  | { type: 'lobby.state'; v: LobbyView }
  | { type: 'chat.msg'; v: ChatMsg }
  | { type: 'reaction'; v: Reaction }
  | { type: 'reaction.expire'; id: number }
  | { type: 'kicked'; reason: string }
  | { type: 'game.start'; v: NonNullable<State['currentMatch']> }
  | { type: 'game.tick'; matchId: string; payload: unknown }
  | { type: 'game.phase'; matchId: string; phase: string; phaseEndsAt?: number; payload?: unknown }
  | { type: 'game.end'; result: RoundResult }
  | { type: 'game.end.unmount' }
  | { type: 'vote.options'; gameIds: string[]; endsAt: number }
  | { type: 'vote.tally'; tally: Record<string, number> }
  | { type: 'set.final'; v: FinalStandings }
  | { type: 'tutorial'; v: { gameId: string; headline: string; tip: string } }
  | { type: 'tutorial.dismiss' }
  | { type: 'jukebox.state'; v: JukeboxState };

function reducer(s: State, a: Action): State {
  switch (a.type) {
    case 'connected':
      // A successful (re)connect clears the reconnecting flag, the failed
      // flag, AND any stale error banner — a prior 'connection-error'/
      // 'no-conn' is meaningless once we're genuinely connected again (it
      // was sticking around indefinitely, e.g. after StrictMode's dev-only
      // double-mount closes the first socket before it opens).
      return { ...s, connected: a.v, reconnecting: a.v ? false : s.reconnecting, failed: a.v ? false : s.failed, error: a.v ? null : s.error };
    case 'reconnecting': return { ...s, reconnecting: a.v };
    case 'failed': return { ...s, failed: true, reconnecting: false, connected: false };
    case 'error': return { ...s, error: a.code };
    case 'lobby.state':
      return {
        ...s,
        lobby: a.v,
        finalStandings: a.v.state === 'finalStandings' ? s.finalStandings : null,
        currentMatch: a.v.state === 'waiting' ? null : s.currentMatch,
        vote: a.v.state === 'waiting' ? null : s.vote,
        // Seed the jukebox from the view for a late joiner; keep the last
        // known state if this view omits it (idle rooms carry no jukebox).
        jukebox: a.v.jukebox ?? s.jukebox,
      };
    case 'chat.msg': return { ...s, chat: [...s.chat.slice(-49), a.v] };
    case 'reaction': return { ...s, reactions: [...s.reactions.slice(-9), a.v] };
    case 'reaction.expire': return { ...s, reactions: s.reactions.filter((r) => r.id !== a.id) };
    case 'kicked': return { ...s, kicked: a.reason };
    case 'game.start':
      return { ...s, currentMatch: a.v, lastTick: null, lastResult: null, vote: null, finalStandings: null, currentPhase: null };
    case 'game.tick':
      if (!s.currentMatch || s.currentMatch.matchId !== a.matchId) return s;
      return { ...s, lastTick: { matchId: a.matchId, payload: a.payload } };
    case 'game.phase':
      return { ...s, currentPhase: { matchId: a.matchId, phase: a.phase, ...(a.phaseEndsAt !== undefined ? { phaseEndsAt: a.phaseEndsAt } : {}), ...(a.payload !== undefined ? { payload: a.payload } : {}) } };
    case 'game.end':
      // Keep currentMatch alive for a few seconds after game.end so
      // the per-game outro card has time to play. The actual unmount
      // happens via the deferred 'game.end.unmount' action below.
      // lastResult is set immediately so post-round UI can read it.
      return { ...s, lastResult: a.result };
    case 'game.end.unmount':
      return { ...s, currentMatch: null };
    case 'vote.options':
      return { ...s, vote: { gameIds: a.gameIds, endsAt: a.endsAt, tally: {} } };
    case 'vote.tally':
      return { ...s, vote: s.vote ? { ...s.vote, tally: a.tally } : s.vote };
    case 'set.final':
      return { ...s, finalStandings: a.v };
    case 'jukebox.state':
      return { ...s, jukebox: a.v };
    case 'tutorial':
      return { ...s, tutorial: a.v };
    case 'tutorial.dismiss':
      return { ...s, tutorial: null };
    default: return s;
  }
}

const INITIAL: State = {
  connected: false, reconnecting: false, failed: false,
  lobby: null, chat: [], reactions: [],
  error: null, kicked: null, currentMatch: null, lastTick: null,
  lastResult: null, vote: null, finalStandings: null, tutorial: null,
  currentPhase: null, jukebox: null,
};

export interface LobbySocketApi extends State {
  ready(next: boolean): void;
  start(): void;
  newSession(): void;
  leave(): void;
  setSettings(patch: Record<string, unknown>): void;
  kick(charId: string): void;
  freeLaunch(gameId: string): void;
  voteCast(gameId: string): void;
  skipRoundEnd(): void;
  sendInput(payload: unknown): void;
  /** Send multiple inputs as one Lambda invocation. The server
   *  iterates them against a single state load. Per
   *  netcode-revamp.md §VIII.5. Each payload gets auto-injected
   *  with its own _seq so the source client can ack each one
   *  independently. */
  sendInputBatch(payloads: unknown[]): void;
  sendHeartbeat(): void;
  sendChat(text: string): void;
  sendReaction(emoji: string): void;
  /** "Your Own Tavern" Phase B — request a jukebox song (by record id). */
  sendJukeboxRequest(recordId: string): void;
  /** Advance the jukebox to the next queued pick (host drives on clip end;
   *  any player after the stale window). */
  sendJukeboxAdvance(): void;
  refreshPresence(): void;
  dismissTutorial(): void;
  rawSend(action: string, extra?: Record<string, unknown>): void;
  /** Highest input seq the server has acknowledged for THIS client.
   *  Per netcode-revamp.md §III.1 and §VIII.1. Used by movement-game
   *  clients to drop replayed-but-acked inputs from their reconciliation
   *  queue. Returns 0 until the server has acked anything. */
  getLastInputAck(): number;
  /** Send a transient waiting-room walk-around position. Fanned out to
   *  other players; never persisted. Returns TRUE when the frame went out
   *  on the gameroom presence lane (callers may then send at up to ~15Hz)
   *  and FALSE on the deprecated Lambda fallback (keep to ≤3Hz). */
  sendMove(x: number, z: number, yaw: number, moving: boolean): boolean;
  /** Subscribe to inbound remote {@link MoveState} updates. The callback fires
   *  per received move (NOT via React state, so it never re-renders the room).
   *  Returns an unsubscribe fn. */
  subscribeMoves(cb: (m: MoveState) => void): () => void;
  /** Trigger a hall toy (dartboard / dice / clink). The actor mints `seed`
   *  and plays optimistically; the server relays it verbatim to everyone else
   *  so the outcome matches. Fire-and-forget on the main socket. */
  sendToyUse(toyId: string, seed: number): void;
  /** Subscribe to inbound remote {@link ToyUse} triggers (NOT via React state).
   *  Returns an unsubscribe fn. */
  subscribeToys(cb: (t: ToyUse) => void): () => void;
}

interface Args {
  wsUrl: string;
  ticket: string;
  enabled: boolean;
  charId: string;
  /** If reconnecting, the last seq we saw. */
  sinceSeq?: number | undefined;
}

export function useLobbySocket({ wsUrl, ticket, enabled, charId, sinceSeq }: Args): LobbySocketApi {
  const wsRef = useRef<WebSocket | null>(null);
  const seqRef = useRef<number>(sinceSeq ?? 0);
  const reactionIdRef = useRef<number>(1);
  /** Per-client outgoing input sequence counter. Bumped on every
   *  sendInput; injected as `_seq` into the payload so the server can
   *  echo it back on the resulting tick as `ack`. */
  const inputSeqRef = useRef<number>(0);
  /** Highest input seq the server has acknowledged for THIS client.
   *  Updated from any inbound game.tick that carries an `ack` field. */
  const lastAckRef = useRef<number>(0);
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const stateRef = useRef<State>(INITIAL);
  /** Remote-move subscribers. Kept OUT of reducer state on purpose: moves
   *  arrive up to ~12/s in a full room and only the 3D backdrop cares, so
   *  routing them through React state would re-render the whole Room per
   *  message. Subscribers read them directly and drive the scene per-frame. */
  const moveSubsRef = useRef<Set<(m: MoveState) => void>>(new Set());
  /** Remote hall-toy subscribers. Kept OUT of reducer state for the same
   *  reason as moves: only the 3D backdrop cares, and routing through React
   *  would re-render the whole Room per trigger. */
  const toySubsRef = useRef<Set<(t: ToyUse) => void>>(new Set());
  /** RT fast-lane (Total-Redesign-Spec §1d a3). Kept in a ref (not React
   *  state) so lane churn never re-renders. Opened on an `rt.offer`; carries
   *  in-match input + snapshot traffic when up, with the main API-GW socket
   *  as the automatic per-slot fallback. Inert until an offer arrives. */
  const rtLaneRef = useRef<RtLane | null>(null);
  /** EMA of (serverClock − clientClock) sampled from `rt.pong`. Written for
   *  future client-side prediction; not consumed yet. */
  const rtClockOffsetRef = useRef<number | null>(null);
  /** 8P Phase 2 — LOBBY presence lane: waiting-room walk sync over the
   *  gameroom fast path (`rt.lobby` offer). Separate from the match lane;
   *  frames carry SLOT (translated to charId against the live roster).
   *  The offer is retained so the lane re-opens when the room returns to
   *  'waiting' after a match (the 3h token outlives rounds). */
  const lobbyLaneRef = useRef<RtLane | null>(null);
  const lobbyOfferRef = useRef<{ roomCode: string; url: string; token: string } | null>(null);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (!enabled || !wsUrl || !ticket) return;

    // R1#7 — exponential-backoff reconnect.
    //
    // Replaces the previous `window.location.reload()` escape hatch.
    // iOS aggressively backgrounds tabs; the old behaviour burned a full
    // bundle reload every time a user came back to the tab, killing
    // animations and audio context. Now we keep the React tree alive and
    // only re-establish the WS layer.
    //
    // Lifecycle:
    //   - `connect(ticketStr)` opens a socket, wires listeners.
    //   - On non-clean close, schedule a reconnect with backoff.
    //   - Reconnect: mint a fresh ticket via joinLobby(roomCode) since
    //     the original ticket is one-shot (burned by ws-connect on
    //     successful handshake), then call connect() with the new ticket.
    //   - After MAX_ATTEMPTS, dispatch 'failed' and stop.
    //   - Cleanup: cancel any pending timer + close any open socket.
    const BACKOFF_MS = [500, 1000, 2000, 4000, 8000, 8000, 8000, 8000];
    const MAX_ATTEMPTS = BACKOFF_MS.length;

    let cancelled = false;
    let attempt = 0;
    let reconnectTimer: number | null = null;
    let hbTimer: number | null = null;
    let livenessTimer: number | null = null;
    /** roomCode captured from inbound lobby.state. Required by the
     *  reconnect path because joinLobby() needs the code; we can't
     *  recover it from the burnt ticket. Falls back to loadRejoin()
     *  when the user reconnects before any lobby.state has arrived
     *  (rare, but possible if the very first connect dies before open). */
    let roomCodeRef: string | null = null;
    /** Wall-clock of the last inbound main-socket `pong` (liveness). Hoisted
     *  to the effect scope so the extracted `handleServerEvent` can update it
     *  while the per-connection liveness timer reads it. Only one main socket
     *  is ever active at a time, and each `open` resets it. */
    let lastPongAt = Date.now();

    function clearTimers() {
      if (hbTimer !== null) { window.clearInterval(hbTimer); hbTimer = null; }
      if (livenessTimer !== null) { window.clearInterval(livenessTimer); livenessTimer = null; }
    }

    function scheduleReconnect(): void {
      if (cancelled) return;
      if (reconnectTimer !== null) return; // already scheduled
      if (attempt >= MAX_ATTEMPTS) {
        dispatch({ type: 'failed' });
        return;
      }
      const delay = BACKOFF_MS[attempt] ?? 8000;
      attempt += 1;
      dispatch({ type: 'reconnecting', v: true });
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        if (cancelled) return;
        void doReconnect();
      }, delay);
    }

    async function doReconnect(): Promise<void> {
      if (cancelled) return;
      // Resolve a roomCode: prefer the one captured from lobby.state
      // (most accurate); fall back to the saved rejoin snapshot.
      const code = roomCodeRef ?? loadRejoin()?.roomCode ?? null;
      if (!code) {
        // No way to mint a fresh ticket — give up. Caller can show the
        // page-reload escape valve via the `failed` state.
        dispatch({ type: 'failed' });
        return;
      }
      try {
        const fresh = await joinLobby(code);
        if (cancelled) return;
        connect(fresh.ticket, fresh.wsUrl);
      } catch {
        if (cancelled) return;
        // Mint failed (network, expired session, room gone). Try again
        // until the cap is hit.
        scheduleReconnect();
      }
    }

    // ── RT fast lane ────────────────────────────────────────────────────
    // The gameroom offers a direct WebSocket (`/rt`) that bypasses the DDB
    // input-queue hop. It is opened on demand from an `rt.offer` event and
    // torn down at match end / cleanup. All state lives in `rtLaneRef`;
    // sends prefer the lane, and inbound frames route through the SAME
    // `handleServerEvent` as the main socket (rt frames carry no `seq`, so
    // the seq-tracking block is a no-op for them).

    function closeRtLane(): void {
      const lane = rtLaneRef.current;
      if (!lane) return;
      // Null the ref first so the lane's own close handler (which fires
      // asynchronously) short-circuits on the `rtLaneRef.current !== lane`
      // guard and does not schedule a reconnect.
      rtLaneRef.current = null;
      lane.closedByUs = true;
      if (lane.pingTimer !== null) { window.clearInterval(lane.pingTimer); lane.pingTimer = null; }
      if (lane.reconnectTimer !== null) { window.clearTimeout(lane.reconnectTimer); lane.reconnectTimer = null; }
      try { lane.ws.close(); } catch { /* */ }
    }

    function connectRtLane(matchId: string, url: string, token: string, attempts: number): void {
      let laneWs: WebSocket;
      try { laneWs = new WebSocket(`${url}?token=${encodeURIComponent(token)}`); }
      catch { return; }
      const lane: RtLane = {
        ws: laneWs, matchId, attempts,
        pingTimer: null, reconnectTimer: null, closedByUs: false, url, token,
      };
      rtLaneRef.current = lane;

      laneWs.addEventListener('open', () => {
        lane.attempts = 0;
        // Keepalive: a 10s ping keeps CloudFront's origin read timeout from
        // idling the socket during quiet moments (menus, countdowns).
        lane.pingTimer = window.setInterval(() => {
          if (laneWs.readyState === WebSocket.OPEN) {
            try { laneWs.send(JSON.stringify({ action: 'rt.ping', t: Date.now() })); } catch { /* */ }
          }
        }, 10_000);
      });

      laneWs.addEventListener('message', (ev) => {
        let m: ServerEvent;
        try { m = JSON.parse(String(ev.data)) as ServerEvent; }
        catch { return; }
        if (m.event === 'rt.welcome') { console.debug('[rt] lane welcome', m); return; }
        if (m.event === 'rt.pong') {
          // Clock-offset sample (EMA) for future prediction use.
          const sample = m.serverNow - (m.t + Date.now()) / 2;
          rtClockOffsetRef.current = rtClockOffsetRef.current === null
            ? sample : rtClockOffsetRef.current * 0.8 + sample * 0.2;
          return;
        }
        handleServerEvent(m);
      });

      // Errors surface as a subsequent close; the close handler owns retry.
      laneWs.addEventListener('error', () => { /* no-op — see close */ });

      laneWs.addEventListener('close', () => {
        if (lane.pingTimer !== null) { window.clearInterval(lane.pingTimer); lane.pingTimer = null; }
        if (rtLaneRef.current !== lane) return; // superseded or closed by us
        if (lane.closedByUs) { rtLaneRef.current = null; return; }
        const stillCurrent = stateRef.current.currentMatch?.matchId === matchId;
        if (stillCurrent && lane.attempts < 4) {
          const delay = [500, 1000, 2000, 4000][lane.attempts] ?? 4000;
          const nextAttempt = lane.attempts + 1;
          lane.reconnectTimer = window.setTimeout(() => {
            if (rtLaneRef.current !== lane) return;
            if (stateRef.current.currentMatch?.matchId !== matchId) { rtLaneRef.current = null; return; }
            connectRtLane(matchId, url, token, nextAttempt);
          }, delay);
        } else {
          rtLaneRef.current = null;
        }
      });
    }

    function openRtLane(matchId: string, url: string, token: string): void {
      // Ops kill switch: `localStorage['tt-rt-off'] = '1'` forces every
      // client back onto the DDB path without a redeploy.
      try { if (localStorage.getItem('tt-rt-off') === '1') return; } catch { /* */ }
      if (!url || !token) return;
      const existing = rtLaneRef.current;
      if (existing && existing.matchId === matchId
        && (existing.ws.readyState === WebSocket.OPEN || existing.ws.readyState === WebSocket.CONNECTING)) {
        return; // a lane for this match is already open/opening
      }
      closeRtLane();
      connectRtLane(matchId, url, token, 0);
    }

    // ── LOBBY presence lane (8P Phase 2) ────────────────────────────────
    // Mirrors the match lane's lifecycle exactly; only the message set
    // differs: outbound `lobby.move` (walk positions), inbound relayed
    // `lobby.move` frames keyed by SLOT. On failure of any kind the
    // Lambda `lobby.move` relay keeps working — feature-degrade, not two
    // live protocols (the Lambda path is deprecated and removal-tracked).

    function closeLobbyLane(): void {
      const lane = lobbyLaneRef.current;
      if (!lane) return;
      lobbyLaneRef.current = null;
      lane.closedByUs = true;
      if (lane.pingTimer !== null) { window.clearInterval(lane.pingTimer); lane.pingTimer = null; }
      if (lane.reconnectTimer !== null) { window.clearTimeout(lane.reconnectTimer); lane.reconnectTimer = null; }
      try { lane.ws.close(); } catch { /* */ }
    }

    function connectLobbyLane(roomCode: string, url: string, token: string, attempts: number): void {
      let laneWs: WebSocket;
      try { laneWs = new WebSocket(`${url}?token=${encodeURIComponent(token)}`); }
      catch { return; }
      const lane: RtLane = {
        ws: laneWs, matchId: roomCode, attempts,
        pingTimer: null, reconnectTimer: null, closedByUs: false, url, token,
      };
      lobbyLaneRef.current = lane;

      laneWs.addEventListener('open', () => {
        lane.attempts = 0;
        lane.pingTimer = window.setInterval(() => {
          if (laneWs.readyState === WebSocket.OPEN) {
            try { laneWs.send(JSON.stringify({ action: 'rt.ping', t: Date.now() })); } catch { /* */ }
          }
        }, 10_000);
      });

      laneWs.addEventListener('message', (ev) => {
        let m: { event?: string; slot?: number; x?: number; z?: number; yaw?: number; moving?: boolean; caps?: string[] };
        try { m = JSON.parse(String(ev.data)) as typeof m; } catch { return; }
        if (m.event === 'rt.welcome') {
          lane.presenceReady = Array.isArray(m.caps) && m.caps.includes('presence');
          if (!lane.presenceReady) console.warn('[rt] lobby lane lacks presence capability — staying on Lambda relay');
          return;
        }
        if (m.event !== 'lobby.move' || typeof m.slot !== 'number') return;
        // Slot → charId against the live roster (the lane doesn't know ids).
        const p = stateRef.current.lobby?.players.find((pl) => pl.slot === m.slot && !pl.isSpectator);
        if (!p || p.charId === charId) return;
        const mv: MoveState = { charId: p.charId, x: m.x ?? 0, z: m.z ?? 0, yaw: m.yaw ?? 0, moving: m.moving === true, at: Date.now() };
        for (const cb of moveSubsRef.current) cb(mv);
      });

      laneWs.addEventListener('error', () => { /* close handler owns retry */ });
      laneWs.addEventListener('close', () => {
        if (lane.pingTimer !== null) { window.clearInterval(lane.pingTimer); lane.pingTimer = null; }
        if (lobbyLaneRef.current !== lane) return;
        if (lane.closedByUs) { lobbyLaneRef.current = null; return; }
        const lob = stateRef.current.lobby;
        const stillWaiting = lob?.roomCode === roomCode && lob?.state === 'waiting';
        if (stillWaiting && lane.attempts < 4) {
          const delay = [500, 1000, 2000, 4000][lane.attempts] ?? 4000;
          const nextAttempt = lane.attempts + 1;
          lane.reconnectTimer = window.setTimeout(() => {
            if (lobbyLaneRef.current !== lane) return;
            connectLobbyLane(roomCode, url, token, nextAttempt);
          }, delay);
        } else {
          lobbyLaneRef.current = null;
        }
      });
    }

    function openLobbyLane(roomCode: string, url: string, token: string): void {
      try { if (localStorage.getItem('tt-rt-off') === '1') return; } catch { /* */ }
      if (!url || !token) return;
      const existing = lobbyLaneRef.current;
      if (existing && existing.matchId === roomCode
        && (existing.ws.readyState === WebSocket.OPEN || existing.ws.readyState === WebSocket.CONNECTING)) {
        return;
      }
      closeLobbyLane();
      connectLobbyLane(roomCode, url, token, 0);
    }

    /**
     * Shared inbound-event handler for BOTH the main socket and the RT lane.
     * Extracted verbatim from the main socket's message listener so its
     * behavior is unchanged; the RT lane routes everything except its own
     * `rt.welcome`/`rt.pong` control frames through here.
     */
    function handleServerEvent(msg: ServerEvent): void {
        if ('seq' in msg && typeof (msg as { seq?: number }).seq === 'number') {
          const eventSeq = (msg as { seq: number }).seq;
          const isGameroomTickSeq = msg.event === 'game.tick' && eventSeq >= 1_000_000;
          if (!isGameroomTickSeq) seqRef.current = Math.max(seqRef.current, eventSeq);
        }
        switch (msg.event) {
          case 'lobby.state': {
            dispatch({ type: 'lobby.state', v: msg.state });
            if (msg.state.roomCode && charId) {
              roomCodeRef = msg.state.roomCode;
              saveRejoin({ roomCode: msg.state.roomCode, charId, lastSeq: seqRef.current, ts: Date.now() });
            }
            // Presence lane rides ONLY the waiting room: (re)open it when
            // the room is waiting (the retained 3h offer covers returns
            // from matches), close it the moment a match takes over.
            if (msg.state.state === 'waiting') {
              const offer = lobbyOfferRef.current;
              if (offer && offer.roomCode === msg.state.roomCode) openLobbyLane(offer.roomCode, offer.url, offer.token);
            } else {
              closeLobbyLane();
            }
            const active = msg.state.activeMatch;
            const current = stateRef.current.currentMatch;
            if (active && (!current || current.matchId !== active.matchId)) {
              const receivedAt = Date.now();
              const cdMs = Math.max(0, active.startsAt - receivedAt);
              const durMs = Math.max(0, active.endsAt - active.startsAt);
              const localStartsAt = receivedAt + cdMs;
              const localEndsAt = localStartsAt + durMs;
              dispatch({
                type: 'game.start',
                v: { matchId: active.matchId, gameId: active.gameId, startsAt: localStartsAt, endsAt: localEndsAt, params: active.params },
              });
            }
            break;
          }
          case 'chat.msg': dispatch({ type: 'chat.msg', v: { charId: msg.charId, name: msg.name, text: msg.text, at: msg.at } }); break;
          case 'jukebox.state': dispatch({ type: 'jukebox.state', v: msg.jukebox }); break;
          case 'reaction': {
            const id = reactionIdRef.current++;
            dispatch({ type: 'reaction', v: { id, charId: msg.charId, name: msg.name, emoji: msg.emoji, at: msg.at } });
            window.setTimeout(() => dispatch({ type: 'reaction.expire', id }), 2000);
            break;
          }
          case 'lobby.move': {
            // Transient walk-around update from another player — never touches
            // React state; dispatched straight to the 3D backdrop's subscribers.
            const m: MoveState = { charId: msg.charId, x: msg.x, z: msg.z, yaw: msg.yaw, moving: msg.moving, at: Date.now() };
            for (const cb of moveSubsRef.current) cb(m);
            break;
          }
          case 'toy.use': {
            // Transient hall-toy trigger — like lobby.move, dispatched straight
            // to the 3D backdrop's subscribers (never React state).
            const t: ToyUse = { charId: msg.charId, name: msg.name, toyId: msg.toyId, seed: msg.seed };
            for (const cb of toySubsRef.current) cb(t);
            break;
          }
          case 'kicked': dispatch({ type: 'kicked', reason: msg.reason }); clearRejoin(); break;
          case 'rt.offer':
            // Open the gameroom fast lane for this match (inert if the ops
            // kill switch is set or a lane for this match is already up).
            openRtLane(msg.matchId, msg.rtUrl, msg.rtToken);
            break;
          case 'rt.lobby':
            // Waiting-room presence lane offer. Retained for the whole
            // session so the lane re-opens on every return to 'waiting'.
            lobbyOfferRef.current = { roomCode: msg.roomCode, url: msg.rtUrl, token: msg.rtToken };
            if (stateRef.current.lobby?.state === 'waiting' || !stateRef.current.lobby) {
              openLobbyLane(msg.roomCode, msg.rtUrl, msg.rtToken);
            }
            break;
          case 'game.start': {
            // CRITICAL: derive startsAt/endsAt from RECEIPT TIME + durations.
            // Server's absolute timestamps drift across devices via clock skew.
            // Relative durations are bulletproof.
            const m = msg as unknown as { matchId: string; gameId: string; countdownMs?: number; durationMs?: number; startsAt: number; endsAt: number; params: unknown };
            const receivedAt = Date.now();
            const cdMs = typeof m.countdownMs === 'number' ? m.countdownMs : Math.max(0, m.startsAt - receivedAt);
            const durMs = typeof m.durationMs === 'number' ? m.durationMs : Math.max(0, m.endsAt - m.startsAt);
            const localStartsAt = receivedAt + cdMs;
            const localEndsAt = localStartsAt + durMs;
            dispatch({ type: 'game.start', v: { matchId: m.matchId, gameId: m.gameId, startsAt: localStartsAt, endsAt: localEndsAt, params: m.params } });
            break;
          }
          case 'game.tick': {
            // Envelope-level ack: server-side handleGameInput attaches
            // `ack` to the tick when the input that caused it carried a
            // `_seq`. Bubble the ack into lastAckRef so movement games
            // can use it for reconciliation. Per netcode-revamp.md §VIII.1.
            const ackField = (msg as { ack?: number }).ack;
            if (typeof ackField === 'number' && ackField > lastAckRef.current) {
              lastAckRef.current = ackField;
            }
            const ackBySlot = (msg as { ackBySlot?: Record<string, number> }).ackBySlot;
            if (ackBySlot) {
              const mySlot = stateRef.current.lobby?.players.find((p) => p.charId === charId)?.slot;
              const slotAck = mySlot !== undefined ? ackBySlot[String(mySlot)] : undefined;
              if (typeof slotAck === 'number' && slotAck > lastAckRef.current) {
                lastAckRef.current = slotAck;
              }
            }
            dispatch({ type: 'game.tick', matchId: msg.matchId, payload: msg.payload });
            break;
          }
          case 'game.phase': {
            const m = msg as unknown as { matchId: string; phase: string; phaseEndsAt?: number; payload?: unknown };
            dispatch({
              type: 'game.phase',
              matchId: m.matchId,
              phase: m.phase,
              ...(m.phaseEndsAt !== undefined ? { phaseEndsAt: m.phaseEndsAt } : {}),
              ...(m.payload !== undefined ? { payload: m.payload } : {}),
            });
            break;
          }
          case 'game.end':
            // Match over — tear down the fast lane (closedByUs=true). Any
            // stray post-end input falls back to the main socket.
            closeRtLane();
            dispatch({ type: 'game.end', result: msg.result });
            feedback.cheer();
            // Defer the actual unmount of the GameStage so the per-game
            // outro card (4s default) has time to slide in, hold, and
            // slide out before the canvas is destroyed. Without this,
            // currentMatch flipped to null instantly on game.end and
            // GameStage's effect cleanup ran the per-game stop() which
            // tore down the Pixi stage AND killed the outro card mid-animation.
            window.setTimeout(() => dispatch({ type: 'game.end.unmount' }), 3200);
            break;
          case 'vote.options': dispatch({ type: 'vote.options', gameIds: msg.gameIds, endsAt: msg.endsAt }); break;
          case 'vote.tally': dispatch({ type: 'vote.tally', tally: msg.tally }); break;
          case 'set.final':
            dispatch({ type: 'set.final', v: { standings: msg.standings, awards: msg.awards, winnerHue: msg.winnerHue } });
            break;
          case 'tutorial':
            dispatch({ type: 'tutorial', v: { gameId: msg.gameId, headline: msg.pitch, tip: msg.tip } });
            break;
          case 'pong': lastPongAt = Date.now(); break;
          case 'error':
            dispatch({ type: 'error', code: msg.code });
            // Server lost our CONN row. Closing triggers the reconnect
            // path which mints a fresh ticket — much cleaner than the
            // page-reload bandaid this used to be.
            if (msg.code === 'no-conn') {
              try { wsRef.current?.close(); } catch { /* */ }
            }
            break;
        }
    }

    function connect(ticketStr: string, wsUrlStr: string): void {
      if (cancelled) return;
      clearTimers();
      const ws = new WebSocket(`${wsUrlStr}?ticket=${encodeURIComponent(ticketStr)}`);
      wsRef.current = ws;

      // `lastPongAt` is hoisted to the effect scope (shared with the
      // extracted handler). Reset it here so a fresh socket starts liveness
      // from "now" rather than inheriting the previous connection's value.
      lastPongAt = Date.now();
      // Liveness check: if we don't see a pong for >6s while the socket
      // claims to be OPEN, treat the link as dead and force-close. The
      // close handler funnels into the reconnect path. (Old code reloaded
      // the page here; reconnect is much cheaper.)
      livenessTimer = window.setInterval(() => {
        if (ws.readyState !== WebSocket.OPEN) return;
        const stale = Date.now() - lastPongAt;
        if (stale > 6000) {
          try { ws.close(); } catch { /* */ }
          // close handler will schedule the reconnect.
        }
      }, 1500);

      ws.addEventListener('open', () => {
        lastPongAt = Date.now();
        // A successful open resets backoff so a long-lived socket that
        // drops once recovers in 500ms instead of inheriting the previous
        // attempt count.
        attempt = 0;
        dispatch({ type: 'connected', v: true });
        // ALWAYS send lobby.join — handleJoin reattaches the seat to the
        // new connId by charId. handleReplay alone leaves the PLAYER row
        // pointed at the dead connId, which manifested as the "infinite
        // connecting screen" bug because the lobby never recognizes the
        // rejoiner. Replay piggybacks for missed events when applicable.
        ws.send(JSON.stringify({ action: 'lobby.join' }));
        // R1#7 — replay covers BOTH the initial-mount sinceSeq AND the
        // accumulated seq from a prior connection that dropped mid-game.
        // seqRef.current is updated on every server event, so it
        // naturally tracks the high-water mark.
        const since = seqRef.current;
        if (since > 0) {
          ws.send(JSON.stringify({ action: 'lobby.replay', sinceSeq: since }));
        }
      });

      ws.addEventListener('close', () => {
        // Tear down per-socket timers first so they don't leak across
        // reconnect attempts.
        clearTimers();
        if (cancelled) return;
        dispatch({ type: 'connected', v: false });
        // Schedule a reconnect — non-clean drop. Intentional teardown
        // sets `cancelled` first via the cleanup function, so a manual
        // leave won't trigger this.
        scheduleReconnect();
      });
      ws.addEventListener('error', () => dispatch({ type: 'error', code: 'connection-error' }));

      ws.addEventListener('message', (ev) => {
        let msg: ServerEvent;
        try { msg = JSON.parse(String(ev.data)) as ServerEvent; }
        catch { return; }
        handleServerEvent(msg);
      });

      // Heartbeat every 1s.
      hbTimer = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action: 'game.heartbeat' }));
      }, 1000);
    }

    // Initial connection uses the seed ticket from props.
    connect(ticket, wsUrl);

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      clearTimers();
      closeRtLane();
      closeLobbyLane();
      lobbyOfferRef.current = null;
      const ws = wsRef.current;
      try { ws?.close(); } catch { /* ignore */ }
      wsRef.current = null;
    };
  }, [wsUrl, ticket, enabled, charId, sinceSeq]);

  function send(action: string, extra: Record<string, unknown> = {}): void {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action, ...extra }));
  }

  /** Send an action on the RT fast lane when it is OPEN. Returns true when
   *  the frame went out on the lane; false tells the caller to fall back to
   *  the main API-GW socket. The gameroom accepts exactly the same
   *  `game.input` / `game.input.batch` shapes on the lane. */
  function sendViaLane(action: string, extra: Record<string, unknown>): boolean {
    const lane = rtLaneRef.current;
    if (!lane || lane.ws.readyState !== WebSocket.OPEN) return false;
    try { lane.ws.send(JSON.stringify({ action, ...extra })); return true; }
    catch { return false; }
  }

  return {
    ...state,
    ready: (next) => send('lobby.ready', { ready: next }),
    start: () => send('lobby.start'),
    newSession: () => send('set.new'),
    leave: () => { send('lobby.leave'); clearRejoin(); },
    setSettings: (patch) => send('lobby.settings.set', { patch }),
    kick: (cid) => send('lobby.kick', { charId: cid }),
    freeLaunch: (gameId) => send('free.launch', { gameId }),
    voteCast: (gameId) => send('vote.cast', { gameId }),
    skipRoundEnd: () => send('roundEnd.skip'),
    sendInput: (payload) => {
      const seq = ++inputSeqRef.current;
      const wrapped = payload && typeof payload === 'object'
        ? { ...(payload as Record<string, unknown>), _seq: seq }
        : { _v: payload, _seq: seq };
      if (!sendViaLane('game.input', { payload: wrapped })) send('game.input', { payload: wrapped });
    },
    sendInputBatch: (payloads) => {
      if (!Array.isArray(payloads) || payloads.length === 0) return;
      const wrapped = payloads.map((payload) => {
        const seq = ++inputSeqRef.current;
        return payload && typeof payload === 'object'
          ? { ...(payload as Record<string, unknown>), _seq: seq }
          : { _v: payload, _seq: seq };
      });
      if (!sendViaLane('game.input.batch', { payloads: wrapped })) send('game.input.batch', { payloads: wrapped });
    },
    sendHeartbeat: () => send('game.heartbeat'),
    sendChat: (text) => send('chat.msg', { text }),
    sendReaction: (emoji) => send('reaction', { emoji }),
    sendJukeboxRequest: (recordId) => send('jukebox.request', { recordId }),
    sendJukeboxAdvance: () => send('jukebox.advance'),
    refreshPresence: () => send('presence.refresh'),
    dismissTutorial: () => dispatch({ type: 'tutorial.dismiss' }),
    rawSend: send,
    getLastInputAck: () => lastAckRef.current,
    sendMove: (x, z, yaw, moving) => {
      // Prefer the gameroom presence lane (8P Phase 2, ~18Hz relay);
      // fall back to the deprecated Lambda relay only while the lane is
      // down (RT off / cold task / reconnecting).
      const lane = lobbyLaneRef.current;
      if (lane && lane.presenceReady && lane.ws.readyState === WebSocket.OPEN) {
        try {
          lane.ws.send(JSON.stringify({ action: 'lobby.move', x, z, yaw, moving }));
          return true;
        } catch { /* fall through to Lambda relay */ }
      }
      send('lobby.move', { x, z, yaw, moving });
      return false;
    },
    subscribeMoves: (cb) => {
      moveSubsRef.current.add(cb);
      return () => { moveSubsRef.current.delete(cb); };
    },
    sendToyUse: (toyId, seed) => send('toy.use', { toyId, seed }),
    subscribeToys: (cb) => {
      toySubsRef.current.add(cb);
      return () => { toySubsRef.current.delete(cb); };
    },
  };
}
